import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
// Wertimport bewusst über den schlanken Einstiegspunkt: `@epasu/core` zöge die
// Datenbank samt better-sqlite3 in den Browser-Bundle.
import { counterpartAddress } from '@epasu/core/counterpart';
import type { Audience } from '@epasu/core';
import { crm, type ContactRow, type MailRow } from '@/lib/crm-client';
import { aktePfad } from '@/lib/contact-path';
import { FirmenFeld } from '@/components/FirmenFeld';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EntityPicker } from '@/components/EntityPicker';

interface Props {
  message: MailRow;
  audiences: Audience[];
  /** „neu“ öffnet gleich das Anlegen — der Knopf „Kontakt anlegen“. */
  initialModus?: 'vorhanden' | 'neu';
  onClose: () => void;
  onDone: () => void;
}

/**
 * Eine nicht zugeordnete Nachricht einem Kunden zuweisen.
 *
 * Zwei Wege, weil es in der Praxis zwei Fälle gibt: Die Gegenseite ist bereits
 * als Kontakt erfasst (dann zuordnen), oder es schreibt jemand Neues (dann
 * gleich anlegen). Beides wirkt auf die ganze Unterhaltung, nicht nur auf die
 * eine Nachricht.
 *
 * Maßgeblich ist die Gegenseite, nicht der Absender. Im Gesendet-Ordner steht
 * im Absender die eigene Adresse — der Dialog bot einen bisher als eigenen
 * Kunden an, und zuordnen ließ sich so gar nichts.
 */
export function AssignMessageDialog({
  message,
  audiences,
  initialModus = 'vorhanden',
  onClose,
  onDone,
}: Props) {
  const [modus, setModus] = useState<'vorhanden' | 'neu'>(initialModus);
  const [contactId, setContactId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [audienceId, setAudienceId] = useState(audiences[0]?.id ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const gegenseite = counterpartAddress(message);
  // Haengt die Mail schon an einer Firma — die Antwort der Schulleiterin an
  // der Schule —, ist das der beste Vorschlag; sonst die Domain.
  const [firma, setFirma] = useState(
    () => message.organizationName ?? firmenVorschlag(gegenseite),
  );
  const [firmaId, setFirmaId] = useState<string | null>(message.organizationId ?? null);

  // Die vorgeschlagene Firma bringt ihre Zielgruppe mit — sonst stuende dort
  // die erste der Liste, und das Anlegen scheiterte am Zielgruppen-Abgleich.
  useEffect(() => {
    if (!message.organizationId) return;
    let abgebrochen = false;
    void crm.organizations.get(message.organizationId).then((o) => {
      if (!abgebrochen && o) setAudienceId(o.audienceId);
    });
    return () => {
      abgebrochen = true;
    };
  }, [message.organizationId]);
  const [adresseMerken, setAdresseMerken] = useState(true);

  /**
   * Wem die Adresse schon gehoert. Ohne diese Pruefung scheiterte „Neu
   * anlegen“ an „bereits einem Kontakt zugeordnet“ — und weil Leads unter
   * Kontakte ausgeblendet sind, liess sich der Besitzer nirgends finden.
   * `undefined` heisst: wird noch nachgeschlagen.
   */
  const [besitzer, setBesitzer] = useState<ContactRow | null | undefined>(undefined);
  useEffect(() => {
    if (!gegenseite) {
      setBesitzer(null);
      return;
    }
    let abgebrochen = false;
    crm.contacts.forAddress(gegenseite).then(
      (k) => {
        if (!abgebrochen) setBesitzer(k);
      },
      () => {
        if (!abgebrochen) setBesitzer(null);
      },
    );
    return () => {
      abgebrochen = true;
    };
  }, [gegenseite]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (modus === 'vorhanden') {
        if (!contactId) {
          setError('Bitte einen Kontakt auswählen.');
          setBusy(false);
          return;
        }
        await crm.mailbox.assign(message.id, contactId);
        // Damit sich derselbe Handgriff nicht bei jeder Antwort wiederholt:
        // beim naechsten Mal erkennt der Postfach-Abgleich die Adresse selbst.
        // Ein Fehlschlag hier darf die Zuordnung nicht umwerfen — die ist
        // gelaufen, und die Adresse ist nur die Bequemlichkeit obendrauf.
        if (adresseMerken && gegenseite) {
          try {
            await crm.contacts.addEmail(contactId, gegenseite);
          } catch {
            /* schon vergeben oder die Hauptadresse — beides kein Grund zum Abbruch */
          }
        }
      } else {
        if (!audienceId) {
          setError('Bitte eine Zielgruppe wählen.');
          setBusy(false);
          return;
        }
        const res = await crm.mailbox.contactFromMessage(message.id, {
          audienceId,
          firstName,
          lastName,
          ...(firmaId ? { organizationId: firmaId } : { organizationName: firma }),
        });
        if (!res) {
          setError('Aus dieser Nachricht lässt sich kein Kontakt anlegen.');
          setBusy(false);
          return;
        }
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zuordnen fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Nachricht zuordnen"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {modus === 'neu' ? 'Kontakt anlegen' : 'Nachricht zuordnen'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {message.direction === 'outbound' ? 'An' : 'Von'} {gegenseite ?? '—'}
        </p>

        {besitzer && (
          <div className="mt-4 rounded-md border border-info/40 bg-info/10 p-3 text-sm">
            <p>
              <span className="font-medium">{gegenseite}</span> gehört schon zu{' '}
              {besitzer.lifecycle === 'lead' ? 'Lead' : 'Kontakt'}{' '}
              <Link
                to={aktePfad(besitzer.id, besitzer.lifecycle)}
                className="font-medium text-brand hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                {besitzer.displayName}
              </Link>
              {besitzer.organizationName ? ` (${besitzer.organizationName})` : ''}.
            </p>
            {!(modus === 'vorhanden' && contactId === besitzer.id) && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => {
                  setModus('vorhanden');
                  setContactId(besitzer.id);
                }}
              >
                Diesem {besitzer.lifecycle === 'lead' ? 'Lead' : 'Kontakt'} zuordnen
              </Button>
            )}
          </div>
        )}

        <div className="mt-4 flex gap-1 rounded-lg border p-1">
          <button
            type="button"
            onClick={() => setModus('vorhanden')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm ${
              modus === 'vorhanden' ? 'bg-primary text-primary-foreground' : ''
            }`}
          >
            Vorhandener Kontakt
          </button>
          <button
            type="button"
            onClick={() => setModus('neu')}
            disabled={!gegenseite}
            title={gegenseite ? undefined : 'Ohne Adresse der Gegenseite nicht möglich'}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm disabled:opacity-50 ${
              modus === 'neu' ? 'bg-primary text-primary-foreground' : ''
            }`}
          >
            Neu anlegen
          </button>
        </div>

        {modus === 'vorhanden' ? (
          <div className="mt-4 space-y-3">
            <EntityPicker
              kind="contact"
              value={contactId || null}
              onChange={(id) => setContactId(id ?? '')}
              placeholder="Kontakt suchen …"
            />

            {/*
              Der eigentliche Nutzen: schreibt jemand von einer zweiten
              Adresse — `sekretariat@` statt `info@` —, findet der
              Postfach-Abgleich ihn kuenftig von selbst, statt die Post bei der
              Firma stranden zu lassen und weiter nachzufassen. Abwaehlbar,
              weil eine weitergeleitete Mail sonst die falsche Adresse dauerhaft
              an den Kontakt haengte.
            */}
            {gegenseite && (
              <label className="flex items-start gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={adresseMerken}
                  onChange={(e) => setAdresseMerken(e.target.checked)}
                />
                <span>
                  <span className="font-medium text-foreground">{gegenseite}</span> künftig
                  diesem Kontakt zuordnen
                </span>
              </label>
            )}
          </div>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Vorname">
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Field>
            <Field label="Nachname">
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Field>
            <Field label="Firma" className="sm:col-span-2">
              <FirmenFeld
                value={firma}
                onChange={setFirma}
                onSelect={(o) => {
                  setFirmaId(o?.id ?? null);
                  // Kontakt und Firma muessen dieselbe Zielgruppe haben.
                  if (o) setAudienceId(o.audienceId);
                }}
              />
            </Field>
            <Field label="Zielgruppe" className="sm:col-span-2">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={audienceId}
                onChange={(e) => setAudienceId(e.target.value)}
              >
                {audiences.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <p className="text-xs text-muted-foreground sm:col-span-2">
              Die Adresse {gegenseite ?? '—'} wird übernommen.
            </p>
          </div>
        )}

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy || (modus === 'neu' && !!besitzer)}>
            {busy
              ? modus === 'neu'
                ? 'Lege an …'
                : 'Ordne zu …'
              : modus === 'neu'
                ? 'Kontakt anlegen'
                : 'Zuordnen'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Rät den Firmennamen aus der Domain — „rektorat@musterschule.de“ wird zu
 * „Musterschule“. Nur ein Vorschlag, der überschrieben werden kann, und nie
 * bei Freemail-Adressen, wo die Domain nichts über die Firma aussagt.
 */
function firmenVorschlag(address: string | null | undefined): string {
  if (!address) return '';
  const domain = address.split('@')[1]?.toLowerCase();
  if (!domain) return '';

  const freemail = [
    'gmail.com',
    'googlemail.com',
    'gmx.de',
    'gmx.net',
    'web.de',
    't-online.de',
    'outlook.com',
    'outlook.de',
    'hotmail.com',
    'hotmail.de',
    'yahoo.com',
    'yahoo.de',
    'icloud.com',
    'me.com',
    'freenet.de',
    'posteo.de',
    'mailbox.org',
  ];
  if (freemail.includes(domain)) return '';

  const kern = domain.split('.')[0] ?? '';
  return kern ? kern.charAt(0).toUpperCase() + kern.slice(1) : '';
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
